import { useState } from 'react';
import { api } from '../lib/api';
import { pushToast } from '../lib/toast';

export function LoginPage() {
  const [busy, setBusy] = useState(false);

  return (
    <div className="relative flex min-h-screen flex-col bg-canvas">
      {/* Atmosphere — 매우 옅은 mint wash, 좌상단에서 시야로 흘러내림 */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div
          className="absolute -left-40 -top-40 h-[420px] w-[420px] rounded-full opacity-70 blur-3xl"
          style={{
            background:
              'radial-gradient(closest-side, rgba(255,107,74,0.28), rgba(255,107,74,0))',
          }}
        />
        <div
          className="absolute -right-32 bottom-[-160px] h-[360px] w-[360px] rounded-full opacity-50 blur-3xl"
          style={{
            background:
              'radial-gradient(closest-side, rgba(232,185,74,0.30), rgba(232,185,74,0))',
          }}
        />
      </div>

      {/* Top brand */}
      <header className="relative z-10 mx-auto flex w-full max-w-[1180px] items-center justify-between px-6 pt-8 sm:pt-10">
        <span className="flex items-center gap-2">
          <span aria-hidden="true" className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inset-0 rounded-full bg-ink-950" />
            <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-coral-500 shadow-[0_0_8px_rgba(255,107,74,0.65)]" />
          </span>
          <span className="text-[14px] font-semibold tracking-[-0.01em] text-ink-950">
            Auto-OMS
          </span>
        </span>
        <span className="label-overline hidden sm:inline">사내 전용 · 초대 계정</span>
      </header>

      {/* Stage */}
      <main className="relative z-10 flex flex-1 items-center justify-center px-6 py-12">
        <div className="flex w-full max-w-[480px] flex-col items-center gap-12">
          {/* Headline */}
          <div className="text-center">
            <p className="label-overline mb-5 inline-flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-coral-500 shadow-[0_0_6px_rgba(255,107,74,0.7)]" />
              Order Operations
            </p>
            <h1
              className="display-2xl text-ink-950 text-[56px] sm:text-[64px]"
              style={{ fontFamily: "'NanumBarunGothic', sans-serif" }}
            >
              오엠스
            </h1>
            <p className="mt-6 max-w-[36ch] text-[15px] leading-[1.65] text-ink-600 mx-auto">
              수집 · 발주 발송 · 송장 회수 · 출고 확정.
              반복 작업을 사라지게 하는 사내 OMS.
            </p>
          </div>

          {/* Card */}
          <div className="card-featured mx-auto w-full max-w-[420px] p-7 sm:p-8">
            <h2 className="text-[18px] font-medium tracking-[-0.005em] text-ink-950">
              로그인
            </h2>
            <p className="mt-2 text-[13px] leading-[1.6] text-ink-500">
              허용된 Google 계정으로만 접근할 수 있습니다.
              관리자에게 초대를 요청하세요.
            </p>

            <button
              className="btn-primary btn-lg mt-7 w-full"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const { url } = await api.get<{ url: string }>('/auth/signin');
                  window.location.href = url;
                } catch (e) {
                  setBusy(false);
                  pushToast(
                    'error',
                    'OAuth 시작에 실패했습니다. 잠시 후 다시 시도해 주세요.',
                  );
                }
              }}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner /> Google로 이동 중…
                </span>
              ) : (
                <>
                  <GoogleMark />
                  Google로 로그인
                </>
              )}
            </button>

          </div>
        </div>
      </main>

      <footer className="relative z-10 mx-auto w-full max-w-[1180px] px-6 pb-6 text-center text-[11px] text-ink-400 sm:text-left">
        © Auto-OMS · internal tool
      </footer>
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.1l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.1z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C40.7 36.3 44 30.7 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

